import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { UserCircle } from '@phosphor-icons/react';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';

/**
 * WHO OWNS THIS TICKET — one definition, rendered in two places.
 *
 * A support queue where every ticket belongs to everybody belongs to nobody. The column
 * `assignedAdminId` and its `assignee` relation already existed and were already returned by BOTH
 * the queue and the detail endpoint — they were simply never rendered, so the data arrived and
 * was silently discarded (Hard Rule #18: the absence of the control IS the defect, and no API
 * test can find it).
 *
 * The name shown here and the name shown on the queue row come from the SAME `assignee` object on
 * the SAME response, so the two surfaces cannot disagree. Eligibility is decided server-side
 * (`ASSIGNABLE_WHERE` in platform-support.service.ts); this picker only offers what that returns
 * and the server re-validates whatever is sent back.
 */

export interface Assignee {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
}

export function assigneeName(a: Assignee | null | undefined): string {
  if (!a) return 'Unassigned';
  return `${a.firstName} ${a.lastName}`.trim() || a.email || 'Unassigned';
}

/** Compact, for a table cell. Full name in the title so truncation never hides who owns it. */
export function AssigneeLabel({ assignee }: { assignee: Assignee | null }) {
  if (!assignee) {
    return <span className="text-plat-faint">Unassigned</span>;
  }
  const full = assigneeName(assignee);
  const short = `${assignee.firstName} ${assignee.lastName?.[0] ? `${assignee.lastName[0]}.` : ''}`.trim();
  return (
    <span className="block max-w-[120px] truncate text-plat-secondary" title={assignee.email ? `${full} · ${assignee.email}` : full}>
      {short}
    </span>
  );
}

/** Shared by the detail control; cached so opening several tickets does not refetch the roster. */
export function useAssignees() {
  return useQuery<Assignee[]>({
    queryKey: ['platform', 'support-assignees'],
    queryFn: async () => {
      const res = await platformApi.get('/support/assignees');
      return res.data?.data ?? res.data;
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * The assignment control on the ticket detail page.
 *
 * A native <select> on purpose: it is keyboard-operable, screen-reader-labelled and themed by the
 * same tokens with no custom widget to get wrong — and it matches the status and priority controls
 * already sitting beside it. "Unassigned" is a real option, not an absence, so handing a ticket
 * back to the pool is as explicit as picking it up.
 */
export function AssigneeControl({
  ticketId,
  assignee,
}: {
  ticketId: string;
  assignee: Assignee | null;
}) {
  const queryClient = useQueryClient();
  const { data: options = [], isLoading } = useAssignees();

  const mutation = useMutation({
    mutationFn: (assignedAdminId: string | null) =>
      platformApi.patch(`/support/${ticketId}`, { assignedAdminId }),
    onSuccess: (_res, assignedAdminId) => {
      toast.success(assignedAdminId === null ? 'Ticket unassigned' : 'Ticket assigned');
      // Refreshes the detail AND the queue row, so the two views of ownership move together.
      queryClient.invalidateQueries({ queryKey: ['platform', 'support'] });
    },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err)),
  });

  /*
    The current assignee may not be in `options` — an admin can be suspended while still holding
    tickets. Keeping them as an explicit option means the select shows the truth ("still owned by
    X") instead of silently snapping to Unassigned and implying a change nobody made.
  */
  const stale = assignee && !options.some((o) => o.id === assignee.id) ? assignee : null;

  /*
    `flex-wrap` + `min-w-0` are load-bearing, not decoration. The Platform Console shell keeps a
    fixed 240px sidebar at every width, so at a 390px viewport this control is handed ~150px. A
    flex child defaults to `min-width: auto` and therefore REFUSES to shrink below its content,
    which pushed the select clean outside the viewport — caught at 390px in the browser journey,
    invisible to every unit test. Wrapping the label and letting the select shrink keeps the
    control usable in whatever space it is given.
  */
  return (
    <div className="flex min-w-0 max-w-full flex-wrap items-center gap-x-2 gap-y-1.5">
      <UserCircle className="h-4 w-4 shrink-0 text-plat-muted" />
      <label htmlFor={`support-assignee-${ticketId}`} className="whitespace-nowrap text-xs text-plat-muted">
        Assigned to
      </label>
      <select
        id={`support-assignee-${ticketId}`}
        aria-label="Assigned support executive"
        title={`Assigned to ${assigneeName(assignee)}`}
        value={assignee?.id ?? ''}
        disabled={isLoading || mutation.isPending}
        onChange={(e) => mutation.mutate(e.target.value === '' ? null : e.target.value)}
        className="h-8 w-full min-w-0 max-w-[220px] rounded-lg border border-plat-border bg-plat-bg px-2 text-xs text-plat-secondary focus:border-plat-focus focus:outline-none focus-visible:ring-2 focus-visible:ring-plat-focus disabled:cursor-not-allowed disabled:text-plat-disabled sm:w-auto"
      >
        <option value="">Unassigned</option>
        {stale && <option value={stale.id}>{assigneeName(stale)} (no longer active)</option>}
        {options.map((o) => (
          <option key={o.id} value={o.id}>{assigneeName(o)}</option>
        ))}
      </select>
    </div>
  );
}
